import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useResolvedAccessLevel } from '../../components/PermissionsContainer';

/** Seeded by migration 448. Must match `core/admin_override.PRO_ADMIN_OVERRIDE_COMPONENT`. */
export const PRO_ADMIN_OVERRIDE_PERMISSION_ID = 'pro-admin-override';

const ACCESS_LEVEL_EDIT = 2;

export interface AdminOverrideState {
  /** Can this RECORD take an override at all? False on templates (their approve/unapprove
   *  flow is the authority) and in create mode (nothing is locked yet). Independent of
   *  the caller's grant, so the button can tell "nothing to override here" apart from
   *  "you don't hold the permission" — only the latter should still show a cog. */
  eligible: boolean;
  /** Does this caller hold `pro-admin-override` at Edit, on an eligible record?
   *  Gates whether the button is usable. */
  available: boolean;
  /** Is override ON, for THIS pro, right now? Never persisted — see the note below. */
  active: boolean;
  /** Turn it on or off. A no-op when `available` is false. */
  setActive: (on: boolean) => void;
}

const DEFAULT: AdminOverrideState = {
  eligible: false,
  available: false,
  active: false,
  setActive: () => {},
};

const Ctx = createContext<AdminOverrideState>(DEFAULT);

/** Admin Override state for the pro being viewed. Safe default (no provider): off and
 *  unavailable, so any tree without the provider behaves exactly as before. */
export const useAdminOverride = (): AdminOverrideState => useContext(Ctx);

/**
 * Admin Override Mode (#33501) — per-pro, per-session.
 *
 * Some fields are locked by the RECORD'S state: the order is invoiced, the leg is paid.
 * That is usually right, but a legitimate one-field correction otherwise has only two
 * bad routes — void and re-do the whole record, or edit the database directly. The
 * second is worse than it looks, because it skips saverules, and saverules are exactly
 * the logic that has to run. Override is the third route: unlock the field, save down
 * the NORMAL path so every rule fires, and tag the write in history.
 *
 * DELIBERATELY NOT PERSISTED. There is no column, no row, no server-side mode record —
 * this is component state and it dies with the page. Navigating to another pro, or
 * closing and reopening this one, returns the locked view and the admin has to turn
 * override on again on purpose. That deliberate re-entry IS the safety mechanism: there
 * is no state to leave switched on by accident and no cleanup job to write.
 *
 * Two boundaries worth keeping in mind when reading consumers of this:
 *
 *   * Override unlocks RECORD STATE, never ACTOR PERMISSION. Holding it does not let a
 *     user edit fields their own permissions withhold (order-lc-fields, leg-editing,
 *     invoice-cod, …). Effective editability stays
 *     `user-may-write AND (not-state-locked OR override)`. Otherwise this stops being an
 *     override and becomes a privilege-escalation switch.
 *   * This is the CLIENT half. Every save that claims an override re-resolves the grant
 *     server-side (core/admin_override.validate_override), so unlocking an input here is
 *     never what authorizes the write.
 */
export function useAdminOverrideState(
  /** Resets the mode whenever it changes — override belongs to one pro. */
  pronumber: number | string | null | undefined,
  /** False for records that cannot take an override (templates, create mode). Templates
   *  have their own approve/unapprove flow and are out of scope. */
  eligible: boolean = true,
): AdminOverrideState {
  // defaultHidden: an ungranted caller resolves to 0, so no grant means no button. This
  // is the inverse of the defaultDisabled scheme used for read-only-until-granted fields
  // — a bypass capability has to fail closed.
  const level = useResolvedAccessLevel(PRO_ADMIN_OVERRIDE_PERMISSION_ID, true);
  const available = eligible && level >= ACCESS_LEVEL_EDIT;

  const [active, setActiveRaw] = useState(false);

  // Switching pros (the drawer's prev/next, a masterpro hop) must not carry override
  // across. Keyed on the pro itself rather than on mount, because ProDetail stays
  // mounted while the pronumber changes underneath it.
  useEffect(() => {
    setActiveRaw(false);
  }, [pronumber]);

  // Losing the grant mid-session (or landing on a template) drops the mode immediately
  // rather than leaving a stale `active` that consumers would still honor.
  useEffect(() => {
    if (!available) setActiveRaw(false);
  }, [available]);

  const setActive = useCallback(
    (on: boolean) => setActiveRaw(available && on),
    [available],
  );

  return useMemo<AdminOverrideState>(
    () => ({ eligible, available, active: available && active, setActive }),
    [eligible, available, active, setActive],
  );
}

/** Delivers the resolved override state to the order form's descendants, so the field
 *  renders, the Invoicing grid and the leg gates all read one source of truth.
 *
 *  Split from the state hook (same shape as OrderFieldDisplayProvider) because ProDetail
 *  both OWNS the state — it feeds `useOrderFieldDisplay` — and provides it. A component
 *  cannot consume a context it renders. */
export const AdminOverrideProvider: React.FC<{
  value: AdminOverrideState;
  children: React.ReactNode;
}> = ({ value, children }) => <Ctx.Provider value={value}>{children}</Ctx.Provider>;
